import type {
  CloudAllowanceStatus,
  CloudCatalog,
  CloudPlan,
  CloudSize,
  CloudWorkspace,
} from "@afar-research/contracts";

/** Admin views work in the ledger's currency; account views show usage, never amounts. */
export const formatDollars = (microdollars: number) => `$${(microdollars / 1000000).toFixed(2)}`;

const subscribedPlan = (allowance: CloudAllowanceStatus): CloudPlan | null =>
  allowance.plans.find((plan) => plan.id === allowance.subscription?.plan) ?? null;

/** The plan that currently admits Cloud Workspaces, if the Subscription is active. */
export function activeCloudPlan(allowance: CloudAllowanceStatus): CloudPlan | null {
  return allowance.subscription?.status === "active" ? subscribedPlan(allowance) : null;
}

/**
 * Why the account cannot use AFAR Cloud at all: it never had a Cloud Subscription, or its Usage
 * Allowance is used up. A canceled Subscription with allowance left is still usable.
 */
export type CloudUsageBlocker = "no-plan" | "used-up";
export function cloudUsageBlocker(allowance: CloudAllowanceStatus): CloudUsageBlocker | null {
  if (allowance.usageLeftPercent === null) return "no-plan";
  if (allowance.usageLeftPercent === 0) return "used-up";
  return null;
}

/** A size as the user picks it: what it gives, never a provider's machine type. */
export const describeSize = (size: CloudSize) =>
  `${size.label} · ${size.vcpu} vCPU · ${size.memoryGb} GB memory`;

/** A size by its catalog id, falling back to the id for a size the catalog no longer lists. */
export function describeSizeId(catalog: CloudCatalog, sizeId: string): string {
  const size = catalog.sizes.find((entry) => entry.id === sizeId);
  return size ? describeSize(size) : sizeId;
}

/** An image by its catalog id, falling back to the id for one the catalog no longer lists. */
export const describeImageId = (catalog: CloudCatalog, imageId: string) =>
  catalog.images.find((image) => image.id === imageId)?.label ?? imageId;

/** A workspace's image, size and disk in one line. */
export const describeWorkspaceSpec = (
  catalog: CloudCatalog,
  workspace: Pick<CloudWorkspace, "image" | "size" | "diskGb">,
) =>
  `${describeImageId(catalog, workspace.image)} · ${describeSizeId(catalog, workspace.size)} · ${workspace.diskGb} GB disk`;

/** What a plan includes, stated as features rather than amounts. */
export function describePlanIncludes(plan: CloudPlan): string {
  return [
    `${plan.workspaceLimit} Cloud Workspace${plan.workspaceLimit === 1 ? "" : "s"}`,
    "any machine size",
    "Codex included",
  ].join(" · ");
}

/**
 * Why a size cannot be rented, or null when it can. Every size the provider sells is rentable;
 * the Usage Allowance alone limits how long.
 */
export function sizeBlocker(allowance: CloudAllowanceStatus, sizeId: string): string | null {
  if (allowance.catalog.sizes.length === 0)
    return "Machine sizes can't be listed right now. Try again in a moment.";
  return allowance.catalog.sizes.some((entry) => entry.id === sizeId) ? null : "Choose a size.";
}

/** Each rentable size with why it cannot be picked, for a picker that disables those. */
export function sizeChoices(allowance: CloudAllowanceStatus) {
  return allowance.catalog.sizes.map((size) => ({
    size,
    blocker: sizeBlocker(allowance, size.id),
  }));
}

/** The size a new workspace starts from: the first rentable one, which is the cheapest. */
export function defaultSize(allowance: CloudAllowanceStatus): string {
  const choices = sizeChoices(allowance);
  return (choices.find((choice) => choice.blocker === null) ?? choices[0])?.size.id ?? "";
}

/** Why a disk size cannot be used: out of the catalog's range, or not a whole number of GB. */
export function diskBlocker(catalog: CloudCatalog, diskGb: number): string | null {
  const { minGb, maxGb } = catalog.disk;
  return Number.isInteger(diskGb) && diskGb >= minGb && diskGb <= maxGb
    ? null
    : `Disk must be a whole number of GB from ${minGb} to ${maxGb}.`;
}

/**
 * Why a new Cloud Workspace cannot be created, stated before the user tries. Without a spec it
 * answers for the account alone: the plan, its workspace limit, and its usage.
 */
export function cloudCreateBlocker(input: {
  allowance: CloudAllowanceStatus;
  workspaceCount: number;
  spec?: { readonly image: string; readonly size: string; readonly diskGb: number };
}): string | null {
  const plan = activeCloudPlan(input.allowance);
  if (!plan) return "Creating a Cloud Workspace needs an active plan. Ask an administrator.";
  if (input.workspaceCount >= plan.workspaceLimit)
    return `The ${plan.label} plan includes ${plan.workspaceLimit} Cloud Workspace${plan.workspaceLimit === 1 ? "" : "s"}. Delete one to create another.`;
  if (cloudUsageBlocker(input.allowance) === "used-up")
    return describeCloudUsageLeft(input.allowance);
  const spec = input.spec;
  if (!spec) return null;
  if (!input.allowance.catalog.images.some((image) => image.id === spec.image))
    return "Choose an image.";
  return (
    sizeBlocker(input.allowance, spec.size) ?? diskBlocker(input.allowance.catalog, spec.diskGb)
  );
}

/**
 * Why a resize cannot be applied: only a stopped workspace resizes, the disk grows but never
 * shrinks, and a new size must be one the provider still sells. Keeping the current size is always
 * allowed, even one no longer sold, so a disk can still grow.
 */
export function cloudResizeBlocker(input: {
  allowance: CloudAllowanceStatus;
  workspace: Pick<CloudWorkspace, "state" | "size" | "diskGb">;
  size: string;
  diskGb: number;
}): string | null {
  const { workspace } = input;
  if (workspace.state !== "stopped") return "Stop the workspace to resize it.";
  if (input.diskGb < workspace.diskGb)
    return `A disk can grow but never shrink below its ${workspace.diskGb} GB.`;
  const disk = diskBlocker(input.allowance.catalog, input.diskGb);
  if (disk) return disk;
  if (input.size !== workspace.size) {
    const size = sizeBlocker(input.allowance, input.size);
    if (size) return size;
  }
  if (input.size === workspace.size && input.diskGb === workspace.diskGb)
    return input.allowance.catalog.sizes.length > 1
      ? "Choose another size or a larger disk."
      : "Choose a larger disk.";
  return null;
}

/** The resize request for what changed, so an unchanged size or disk is left out. */
export function resizeChange(
  workspace: Pick<CloudWorkspace, "size" | "diskGb">,
  next: { readonly size: string; readonly diskGb: number },
): { size?: string; diskGb?: number } {
  return {
    ...(next.size === workspace.size ? {} : { size: next.size }),
    ...(next.diskGb === workspace.diskGb ? {} : { diskGb: next.diskGb }),
  };
}

/**
 * The rates an administrator sees: each size's hour and a GB of disk for a day. Null for
 * everyone else, who never sees an amount (ADR-0130).
 */
export function describeCloudPrices(allowance: CloudAllowanceStatus): string | null {
  const prices = allowance.prices;
  if (!prices) return null;
  return [
    ...allowance.catalog.sizes.map(
      (size) =>
        `${size.label} ${prices.sizeHourly[size.id] === undefined ? "no rate" : `${formatDollars(prices.sizeHourly[size.id]!)}/hour`}`,
    ),
    `disk ${formatDollars(prices.diskGbDaily)}/GB/day`,
  ].join(" · ");
}

/**
 * The share of the Usage Allowance left, in the words the account sees. Never an amount: those
 * are for administrators, and only in the administration views (ADR-0130).
 */
export function describeCloudUsageLeft(allowance: CloudAllowanceStatus): string | null {
  const left = allowance.usageLeftPercent;
  if (left === null) return null;
  if (allowance.subscription?.status === "canceled") {
    return left === 0 ? "The remaining usage is used up." : `${left}% of the remaining usage left`;
  }
  return left === 0
    ? "This month's usage is used up. It resets when the plan renews."
    : `${left}% of this month's usage left`;
}

/** One line describing the Subscription for the account's own Cloud panel. */
export function describeCloudSubscription(allowance: CloudAllowanceStatus, locale?: string) {
  const subscription = allowance.subscription;
  if (!subscription) return "No plan. An administrator can attach one to this account.";
  const label = subscribedPlan(allowance)?.label ?? subscription.plan;
  if (subscription.status === "canceled")
    return `${label} plan canceled. Remaining usage can still be used; it no longer resets.`;
  const resets =
    subscription.renewsAt === null
      ? ""
      : ` Usage resets ${new Date(subscription.renewsAt).toLocaleDateString(locale)}.`;
  return `${label} plan.${resets}`;
}

/**
 * The Cloud Plan and the share of usage left in one line, for the Cloud Workspace lists. The full
 * meter lives under Settings → Account.
 */
export function describeCloudUsageLine(allowance: CloudAllowanceStatus, locale?: string): string {
  const subscription = allowance.subscription;
  const left = allowance.usageLeftPercent;
  if (!subscription || left === null) return describeCloudSubscription(allowance, locale);
  const label = subscribedPlan(allowance)?.label ?? subscription.plan;
  if (subscription.status === "canceled")
    return [
      label,
      "canceled",
      left === 0 ? "the remaining usage is used up" : `${left}% of the remaining usage left`,
    ].join(" · ");
  return [
    label,
    left === 0 ? "this month's usage is used up" : `${left}% of this month's usage left`,
    ...(subscription.renewsAt === null
      ? []
      : [
          `resets ${new Date(subscription.renewsAt).toLocaleDateString(locale, { day: "numeric", month: "short" })}`,
        ]),
  ].join(" · ");
}

/**
 * Whether to say that a used-up account's workspaces are deleted after the retention period: only
 * once usage is used up or a workspace is already due for deletion, not as standing small print.
 */
export function shouldWarnAboutRetention(
  allowance: CloudAllowanceStatus | null,
  workspaces: ReadonlyArray<Pick<CloudWorkspace, "deleteAfter">>,
): boolean {
  return (
    allowance?.usageLeftPercent === 0 ||
    workspaces.some((workspace) => workspace.deleteAfter !== null)
  );
}

type WorkspaceState = CloudWorkspace["state"];

/** What a workspace is doing, in the words the Cloud panel shows. */
export function describeWorkspaceState(workspace: Pick<CloudWorkspace, "state" | "error">): string {
  const labels: Record<WorkspaceState, string> = {
    provisioning: "Preparing",
    stopped: "Stopped",
    starting: "Starting",
    running: "Running",
    stopping: "Stopping",
    deleting: "Deleting",
    deleted: "Deleted",
  };
  const label = labels[workspace.state];
  return workspace.error === "run_failed"
    ? `${label} · the last run failed to start`
    : workspace.error
      ? `${label} · retry needed`
      : label;
}

/**
 * Where an opened workspace stands while the panel waits to connect: ready once its machine runs,
 * still waiting while it starts, and failed when the start ended without it.
 */
export function openProgress(
  workspace: Pick<CloudWorkspace, "state"> | undefined,
): "ready" | "wait" | "failed" {
  if (!workspace) return "failed";
  if (workspace.state === "running") return "ready";
  // A run still stopping is replaced once it exits, because the workspace is now wanted running.
  if (["starting", "provisioning", "stopping"].includes(workspace.state)) return "wait";
  return "failed";
}

/**
 * How long the panel waits for a started run before telling the user to check back: a first start
 * clones, reshapes and enrolls a machine, which Connect allows fifteen minutes (ADR-0152).
 */
export const OPEN_TIMEOUT_MS = 15 * 60_000;

/** How often the panel reads the list while a workspace is starting, stopping or deleting. */
export const CLOUD_WORKSPACE_REFRESH_MS = 5000;

/** A change the panel has asked Connect to make to one workspace and is still waiting on. */
export type CloudWorkspaceRequest = "open" | "stop" | "delete" | "resize";

const requestLabels: Record<CloudWorkspaceRequest, string> = {
  open: "Starting…",
  stop: "Stopping…",
  delete: "Deleting…",
  resize: "Resizing…",
};
const transitionLabels: Partial<Record<WorkspaceState, string>> = {
  provisioning: "Preparing…",
  starting: "Starting…",
  stopping: "Stopping…",
  deleting: "Deleting…",
};

/**
 * What a row is busy with, or null once it has settled. A request this panel sent wins; otherwise
 * the server's state speaks, so a panel reopened mid-change still shows it.
 */
export function workspaceActivity(
  workspace: Pick<CloudWorkspace, "state">,
  request: CloudWorkspaceRequest | undefined,
): string | null {
  if (request === "open" && workspace.state === "running") return "Connecting…";
  return request ? requestLabels[request] : (transitionLabels[workspace.state] ?? null);
}

/**
 * Whether Start and connect applies: never while the workspace is changing, so a run already
 * coming up is waited on, not started again. Connect follows once it is running.
 */
export function canStartWorkspace(workspace: Pick<CloudWorkspace, "state">): boolean {
  return workspace.state !== "deleted" && transitionLabels[workspace.state] === undefined;
}

/**
 * What a workspace row shows: its status (a change in flight first), and its one prominent action.
 * A workspace this device does not use yet is connected (started first when it is not running).
 * One it already uses is only ever started: once it runs, its Environment row shows the
 * connection, so the cloud row offers nothing more. The picker describes every row it lists as
 * not used here, since it connects them all. Nothing is offered while a change is in flight.
 */
export function describeWorkspaceRow(
  workspace: Pick<CloudWorkspace, "state" | "error">,
  device: { readonly usedHere: boolean; readonly request?: CloudWorkspaceRequest | undefined },
): {
  readonly status: string;
  readonly busy: boolean;
  readonly primary: "connect" | "start" | null;
} {
  const activity = workspaceActivity(workspace, device.request);
  const primary =
    activity !== null || !canStartWorkspace(workspace)
      ? null
      : !device.usedHere
        ? "connect"
        : workspace.state === "running"
          ? null
          : "start";
  return {
    status: activity ?? describeWorkspaceState(workspace),
    busy: activity !== null,
    primary,
  };
}

/**
 * Whether this device uses a workspace. A device that never read the account's listing uses none
 * yet, as First Workspace Setup also reads it.
 */
export const isWorkspaceUsedHere = (added: ReadonlyArray<string> | undefined, id: string) =>
  (added ?? []).includes(id);

/**
 * The workspaces the picker offers: every one that still exists. A workspace this device already
 * uses is offered too, since its connection may be off with nothing else on the page to turn it
 * back on; connecting it selects it again rather than adding it twice.
 */
export function workspacesToConnect<Workspace extends Pick<CloudWorkspace, "state">>(
  workspaces: ReadonlyArray<Workspace>,
): ReadonlyArray<Workspace> {
  return workspaces.filter(
    (workspace) => workspace.state !== "deleting" && workspace.state !== "deleted",
  );
}

/**
 * Whether the panel should keep reading the list: some row is still changing, and no start in
 * flight is already reading it.
 */
export function shouldRefreshCloudWorkspaces(
  workspaces: readonly Pick<CloudWorkspace, "state">[],
  requests: ReadonlyMap<string, CloudWorkspaceRequest>,
): boolean {
  if ([...requests.values()].includes("open")) return false;
  return workspaces.some((workspace) => transitionLabels[workspace.state] !== undefined);
}
